/**
 * Feature 014: Service Provider Engagements
 * Custom hooks for consultation request management
 * Uses React Query for data fetching and caching
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as consultationsApi from '../api/consultationsApi';
import type {
  CreateConsultationRequestDto,
  UpdateNotesDto,
  DeclineEngagementDto,
} from '../types';

// Query keys for React Query cache management
export const consultationsKeys = {
  all: ['consultations'] as const,
  providerRequests: (status?: string) =>
    [...consultationsKeys.all, 'provider', status] as const,
  myRequests: () => [...consultationsKeys.all, 'my-requests'] as const,
  duplicateCheck: (careSeekerUserId: string, providerUserId: string) =>
    [...consultationsKeys.all, 'duplicate-check', careSeekerUserId, providerUserId] as const,
  dailyCount: (careSeekerUserId: string) =>
    [...consultationsKeys.all, 'daily-count', careSeekerUserId] as const,
};

/**
 * Hook to fetch consultation requests for a service provider
 * Uses current user from JWT token automatically
 *
 * @param status - Optional status filter
 * @param enabled - Whether to run the query
 */
export const useProviderConsultationRequests = (
  status?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: consultationsKeys.providerRequests(status),
    queryFn: () => consultationsApi.getProviderConsultationRequests(status),
    enabled,
  });
};

/**
 * Hook to fetch care seeker's own consultation requests
 * Displays in "My Requests" section of Care Seeker dashboard
 */
export const useMyConsultationRequests = (enabled: boolean = true) => {
  return useQuery({
    queryKey: consultationsKeys.myRequests(),
    queryFn: consultationsApi.getMyConsultationRequests,
    enabled,
  });
};

/**
 * Hook to create a new consultation request
 * Rate limiting: Max 1 pending per provider, max 10/day globally
 */
export const useCreateConsultationRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConsultationRequestDto) =>
      consultationsApi.createConsultationRequest(data),
    onSuccess: () => {
      // Invalidate all consultation queries
      queryClient.invalidateQueries({
        queryKey: consultationsKeys.all
      });
    },
  });
};

/**
 * Hook to accept a consultation request
 * Moves engagement to Accepted section, marks conversation as "Active Engagement"
 */
export const useAcceptConsultationRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => consultationsApi.acceptConsultationRequest(id),
    onSuccess: () => {
      // Invalidate all provider consultation queries to refetch
      queryClient.invalidateQueries({ queryKey: consultationsKeys.all });
    },
  });
};

/**
 * Hook to decline a consultation request
 * Engagement disappears, reason sent as message to care seeker
 */
export const useDeclineConsultationRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeclineEngagementDto }) =>
      consultationsApi.declineConsultationRequest(id, data),
    onSuccess: () => {
      // Invalidate all provider consultation queries to refetch
      queryClient.invalidateQueries({ queryKey: consultationsKeys.all });
    },
  });
};

/**
 * Hook to update provider private notes
 * Notes are only visible to the provider
 */
export const useUpdateConsultationNotes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNotesDto }) =>
      consultationsApi.updateConsultationNotes(id, data),
    onSuccess: () => {
      // Invalidate provider consultation queries to refetch
      queryClient.invalidateQueries({ queryKey: consultationsKeys.all });
    },
  });
};

/**
 * Hook to check for duplicate pending consultation request
 * Prevents care seekers from sending multiple requests to same provider
 *
 * @param careSeekerUserId - Care seeker's user ID
 * @param providerUserId - Provider's user ID
 * @param enabled - Whether to run the query
 */
export const useCheckDuplicateRequest = (
  careSeekerUserId: string,
  providerUserId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: consultationsKeys.duplicateCheck(careSeekerUserId, providerUserId),
    queryFn: () => consultationsApi.checkDuplicateRequest(careSeekerUserId, providerUserId),
    enabled: enabled && !!careSeekerUserId && !!providerUserId,
  });
};

/**
 * Hook to check daily consultation request count
 * Enforces rate limit of max 10 requests per day
 *
 * @param careSeekerUserId - Care seeker's user ID
 * @param enabled - Whether to run the query
 */
export const useCheckDailyRequestCount = (
  careSeekerUserId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...consultationsKeys.all, 'daily-count', careSeekerUserId],
    queryFn: () => consultationsApi.checkDailyRequestCount(careSeekerUserId),
    enabled: enabled && !!careSeekerUserId,
  });
};
